/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function kikNumberOnly(value) {
    // Remove non-numeric characters except for '.' (decimal point) and ',' (thousands separator)
    let cleanedValue = value.replace(/[^\d.,]/g, '');

    // Remove commas (thousands separators)
    cleanedValue = cleanedValue.replace(/,/g, '');

    // Convert the cleaned value to a number
    let validNumber = parseFloat(cleanedValue);

    // If it's a valid number, return it, otherwise return NaN
    return isNaN(validNumber) ? null : validNumber;

}

function kikIsEmptyObject(obj) {
    return Object.keys(obj).length === 0;
}

class KikReadMoreLess extends HTMLElement {
    constructor() {
        super();

        // Initialize the state and reference the content and button
        this.isExpanded = false;
        this.content = this.querySelector('.kik-readml-wrapper');
        this.toggleButton = this.querySelector('.kik-button'); // Selecting the button itself, not just the label
        this.cartSummary = this.querySelector('.cart-summary');

        this.disabled = this.dataset.disabled === 'true';
        
        if(this.disabled){
          this.toggleButton.classList.add('hidden');
          this.content.style.maxHeight = 'none';
          return;
        }

        // Parse the JSON configuration
        this.config = JSON.parse(this.querySelector('script[type="application/json"][data-readml]').textContent);
        if (!this.config || kikIsEmptyObject(this.config)) return;

        this.readmoreText = this.config.readmore_text;
        this.readlessText = this.config.readless_text;

        // Media query breakpoints and heights
        this.heights = this.config.height;
        this.mediaQueries = this.config.media_queries;

        // Set initial target height
        this.targetHeight = kikNumberOnly(this.getTargetHeight());
    }

    connectedCallback() {
      this.disabled = this.dataset.disabled === 'true';
      if(!this.disabled){
        // Handle screen resize to adjust height
        window.addEventListener('resize', this.updateHeight.bind(this));

        // Set the initial height and content visibility
        this.updateHeight();

        // Add the click event listener to toggle the content
        this.toggleButton.addEventListener('click', this.toggleContent.bind(this));
      }
    }

    getTargetHeight() {
        const mobileQuery = window.matchMedia(this.mediaQueries.mobile);
        const tabletQuery = window.matchMedia(this.mediaQueries.tablet);
        const desktopQuery = window.matchMedia(this.mediaQueries.desktop);

        if (desktopQuery.matches) {
            return this.heights.desktop;
        } else if (tabletQuery.matches) {
            return this.heights.tablet;
        } else if (mobileQuery.matches) {
            return this.heights.mobile;
        }
    }

    updateHeight() {
        // Update the target height based on current screen size
        this.targetHeight = kikNumberOnly(this.getTargetHeight());

        // Check if content needs to be expanded or hidden based on height
        if ((this.content.scrollHeight - this.cartSummary.scrollHeight) <= this.targetHeight || this.targetHeight <= 0) {
            // Content fits within target height, no toggle needed
            this.toggleButton.classList.add('hidden'); // Hide the button if not needed
            this.content.style.maxHeight = 'none'; // Allow full content display
        } else {
            // Content exceeds target height, show the button
            this.toggleButton.classList.remove('hidden'); // Show the button if needed
            if (!this.isExpanded) {
                this.content.style.maxHeight = `${this.targetHeight}px`; // Apply the restricted height
                this.content.style.overflow = 'hidden';
            }
        }
    }

    toggleContent(event) {
        // Prevent the default button action (like submitting a form)
        event.preventDefault();

        this.isExpanded = !this.isExpanded;

        // Toggle expanded class and button text
        if (this.isExpanded) {
            this.content.style.maxHeight = 'none';
            this.toggleButton.querySelector('.kik-button__label').textContent = `${this.readlessText}`;
            this.toggleButton.querySelector('.kik-button__label').classList.add('kik-button__label--expanded');
        } else {
            this.content.style.maxHeight = `${this.targetHeight}px`;
            this.toggleButton.querySelector('.kik-button__label').classList.remove('kik-button__label--expanded');
            this.toggleButton.querySelector('.kik-button__label').textContent = `${this.readmoreText}`;

            setTimeout(() => {
                this.content.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
            }, 50);
        }
    }

    disconnectedCallback() {
        this.toggleButton.removeEventListener('click', this.toggleContent);
        window.removeEventListener('resize', this.updateHeight);
    }
}

// Define the custom element
customElements.define('kik-readml', KikReadMoreLess);



class KikMultipleVariantQuickorder extends HTMLElement {
    constructor() {
        super();
        this.productFrom = document.getElementById(`${this.dataset.productFormId}`);
        if(!this.productFrom)return;
    }

    connectedCallback() {
        this.productFrom.querySelectorAll('.quantity-increase').forEach(button => {
            button.addEventListener('click', (e) => {
              const input = e.currentTarget.parentNode.querySelector('input');
              const min = parseInt(input.getAttribute('min')) || 0;
              const max = parseInt(input.getAttribute('max'));
              let newValue = parseInt(input.value) + 1;
              if (typeof max === 'number' && !isNaN(max) && newValue > max) {
                newValue = max;
              }
              input.value = newValue;
              this.syncSameInput(input);
              this.updateTotal();
              this.updateQuantityButtonState(input);
            });
        });

        this.productFrom.querySelectorAll('.quantity-decrease').forEach(button => {
            button.addEventListener('click', (e) => {
              const input = e.currentTarget.parentNode.querySelector('input');
              const min = parseInt(input.getAttribute('min')) || 0;
              let newValue = Math.max(parseInt(input.value) - 1, min);
              input.value = newValue;
              this.syncSameInput(input);
              this.updateTotal();
              this.updateQuantityButtonState(input);
            });
        });
          
        this.productFrom.querySelectorAll('input[type="number"]').forEach(input => {
            // Clamp value on input
            input.addEventListener('input', () => {
                const min = parseInt(input.getAttribute('min')) || 0;
                const max = parseInt(input.getAttribute('max'));
                let value = parseInt(input.value) || 0;
                if (value < min) value = min;
                if (typeof max === 'number' && !isNaN(max) && value > max) value = max;
                input.value = value;
                this.updateQuantityButtonState(input);
            });
            // Clamp value and sync on change
            input.addEventListener('change', () => {
                const min = parseInt(input.getAttribute('min')) || 0;
                const max = parseInt(input.getAttribute('max'));
                let value = parseInt(input.value) || 0;
                if (value < min) value = min;
                if (typeof max === 'number' && !isNaN(max) && value > max) value = max;
                input.value = value;
                this.syncSameInput(input);
                this.updateTotal();
                this.updateQuantityButtonState(input);
            });
            // Set initial state
            this.updateQuantityButtonState(input);
        });

        this.productFrom.querySelectorAll('.variant-color-button').forEach(variantButton => {
          variantButton.addEventListener('click', (e) => {
              document.querySelectorAll('.variant-color-button').forEach(btn => btn.classList.remove('selected'));
              variantButton.classList.add('selected');
              this.syncVariantButton(variantButton);
              // Parse variant data from <script> inside the button
              const script = variantButton.querySelector('script[type="application/json"]');
              if (!script) return;

              const variantData = JSON.parse(script.textContent);

              // Update the selected color text
              const selectedColorSpans = document.querySelectorAll('.variant-color-selected span');
              if (selectedColorSpans) {
                selectedColorSpans.forEach(selectedColorSpan => {
                  let colorValue = variantButton.querySelector('span')?.textContent?.trim();
                  if (!colorValue) {
                    const colorSwatchElement = variantButton.closest('[data-colorswatch-value]');
                    colorValue = colorSwatchElement?.dataset.colorswatchValue;
                  }
                  
                  // If still no value, try variant data
                  if (!colorValue && variantData.option1) {
                    colorValue = variantData.option1;
                  }
                  selectedColorSpan.textContent = colorValue;
                });
              }

              // Dispatch the event
              this.productFrom.dispatchEvent(new CustomEvent("variant:change", {
                  bubbles: true,
                  detail: {
                      formId: this.productFrom.id,
                      variant: variantData
                  }
              }));
          });
        });
    }

    updateQuantityButtonState(input) {
        const min = parseInt(input.getAttribute('min')) || 0;
        const max = parseInt(input.getAttribute('max'));
        const value = parseInt(input.value) || 0;
        const decreaseBtn = input.parentNode.querySelector('.quantity-decrease');
        const increaseBtn = input.parentNode.querySelector('.quantity-increase');
        if (decreaseBtn) {
            decreaseBtn.disabled = value <= min;
        }
        if (increaseBtn) {
            if (typeof max === 'number' && !isNaN(max)) {
                increaseBtn.disabled = value >= max;
            } else {
                increaseBtn.disabled = false;
            }
        }
    }

    updateTotal() {
        let totalItems = 0;
        let totalPrice = 0;
        let isTiered = this.productFrom?.dataset.productTieredDiscount === 'true';
      
        let discount = 0;
      
        // Step 1: Calculate total quantity (used in both modes)
        this.productFrom.querySelectorAll('.variant-row').forEach(row => {
          const quantity = parseInt(row.querySelector('input').value);
          if (quantity > 0) {
            totalItems += quantity;
          }
        });
      
        // Step 2: If tiered discount enabled, set discount (in cents)
        // Tiers: 1-4 pcs = 0, 5-24 pcs = 70, 25-144 pcs = 170, 144+ pcs = 350
        if (isTiered) {
          if (totalItems > 144) {
            discount = 350;
          } else if (totalItems >= 25) {
            discount = 170;
          } else if (totalItems >= 5) {
            discount = 70;
          } else {
            discount = 0;
          }
        }
      
        // Step 3: Calculate total price based on mode
        // Note: price from data-price is in cents (Shopify's smallest currency unit)
        this.productFrom.querySelectorAll('.variant-row').forEach(row => {
          const quantity = parseInt(row.querySelector('input').value);
          if (quantity > 0) {
            const priceElement = row.querySelector('.variant-price');
            // Use data-price attribute (in cents) for accurate calculation
            const price = parseInt(priceElement.dataset.price || 0);

            if (isTiered) {
                // discount is in cents (same unit as price)
                const finalUnitPrice = price - discount;
                totalPrice += finalUnitPrice * quantity;
            } else {
                totalPrice += price * quantity;
            }
          }
        });
      
        // Step 4: Update summary
        this.productFrom.querySelector('.total-items').textContent = totalItems + ' ' + (totalItems <= 1 ? 'Piece' : 'Pieces');
        this.syncSummary(this.productFrom.querySelector('.total-items'), totalItems + ' ' + (totalItems <= 1 ? 'Piece' : 'Pieces'));
        // totalPrice is already in cents, formatMoney expects cents
        const formattedPrice = typeof formatMoney === 'function' ? formatMoney(totalPrice) : '$' + (totalPrice / 100).toFixed(2);
        this.productFrom.querySelector('.total-price').textContent = formattedPrice;
        this.syncSummary(this.productFrom.querySelector('.total-price'), formattedPrice);

        // Step 5: Update notes
        this.notes = this.productFrom.querySelectorAll('.multiple-variant-quickorder__note');
        this.notes && this.notes.forEach(note => {
          if(totalItems <= 0){
            note.classList.add('multiple-variant-quickorder__note--show');
          }else{
            note.classList.remove('multiple-variant-quickorder__note--show');
          }
        });
    }

    syncVariantButton(element) {
      const dataSameElement = element.getAttribute('data-same-input');
      const dataTemplate = element.getAttribute('data-template');

      if (!dataSameElement || !dataTemplate) return;
      // Remove -quick-buy-${dataTemplate} from the dataSameInput
      const quickBuyPrefix = `quick-buy-${dataTemplate}`;
      let baseSameInput = dataSameElement;
      if (dataSameElement.includes(quickBuyPrefix)) {
          baseSameInput = dataSameElement.replace(quickBuyPrefix, 'main');
      }

      let similarElements = document.querySelectorAll(`[data-same-input='${baseSameInput}']`);
      if (similarElements) {
        similarElements.forEach(similarElement => {
          if(similarElement != element) {
            similarElement.classList.add('selected');
          }
        })
      }
    }

    syncSummary(element, value) {
      const dataSameElement = element.getAttribute('data-same-input');
      const dataTemplate = element.getAttribute('data-template');
      if (!dataSameElement || !dataTemplate) return;
      // Remove -quick-buy-${dataTemplate} from the dataSameInput
      const quickBuyPrefix = `quick-buy-${dataTemplate}`;
      let baseSameInput = dataSameElement;
      if (dataSameElement.includes(quickBuyPrefix)) {
          baseSameInput = dataSameElement.replace(quickBuyPrefix, 'main');
      }
      // Find the input with the baseSameInput value
      let similarElements = document.querySelectorAll(`[data-same-input='${baseSameInput}']`);
      if (similarElements) {
        similarElements.forEach(similarElement => {
          if(similarElement != element) {
            similarElement.textContent = value;
          }
        })
      }
    }
      
    /**
     * Synchronize the value of the input with its counterpart without the -quick-buy-${data-template} part
     */
    syncSameInput(input) {
      const dataSameInput = input.getAttribute('data-same-input');
      const dataTemplate = input.getAttribute('data-template');
      if (!dataSameInput || !dataTemplate) return;
      // Remove -quick-buy-${dataTemplate} from the dataSameInput
      const quickBuyPrefix = `quick-buy-${dataTemplate}`;
      let baseSameInput = dataSameInput;
      if (dataSameInput.includes(quickBuyPrefix)) {
          baseSameInput = dataSameInput.replace(quickBuyPrefix, 'main');
      }
      // Find the input with the baseSameInput value
      let similarInput = document.querySelector(`input[data-same-input='${baseSameInput}']`);
      if (similarInput && similarInput !== input) {
        similarInput.value = input.value;
        // Dispatch a change event on the similar input
        const event = new Event('change', { bubbles: true });
        similarInput.dispatchEvent(event);
      }
    }
}

// Define the custom element
customElements.define('multiple-variant-quickorder', KikMultipleVariantQuickorder);

class KikScrollToTarget extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('click', (event) =>{
            this.onClickHandler(event);
        })
    }

    onClickHandler(event){
        event.preventDefault();
        this.preventStickyHeader();

        this.targetElement = document.querySelector(`${this.dataset.targetElement}`);
        if(!this.targetElement) return;
        this.targetElement.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });

        // For SideNav Button
        if(this.dataset.type === "kik-sidenav-button"){
            this.similarSidenavButtons = document.querySelectorAll(`.${this.dataset.targetElementClass}`);
            this.similarSidenavButtons && this.similarSidenavButtons.forEach(e => {
                if(e != event.currentTarget){
                    e.classList.remove('kik-sidenav-btn--active');
                }else{
                    e.classList.add('kik-sidenav-btn--active');
                }
            })
        }


    }

    preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }
}

customElements.define('kik-scroll-to-target', KikScrollToTarget);

class KikProductDetails extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }
  
    connectedCallback() {
      const template = this.querySelector('template');
      if (!template) return;
  
      const rawHTML = template.innerHTML;
      const parsedHTML = this.parseContent(rawHTML);
  
      const styles = `
        <style>
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 1rem;
          }
          td {
            padding: 6px 10px;
            vertical-align: top;
          }
          td:first-child {
            font-weight: bold;
            width: 150px;
            white-space: nowrap;
          }
        </style>
      `;
  
      this.shadowRoot.innerHTML = `
        ${styles}
        <div class="product-details">
          <table>
            ${parsedHTML}
          </table>
        </div>
      `;
    }
  
    parseContent(rawHTML) {
      // Replace <br> and paragraph ends with newlines, remove other tags
      const cleanedText = rawHTML
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/\r/g, '')
        .trim();
  
      const sections = cleanedText.split('#######').map(s => s.trim()).filter(Boolean);
  
      const parsed = sections.map(section => {
        const [titlePart, ...valueParts] = section.split('-------');
        const title = titlePart?.trim();
        const value = valueParts.join('-------').trim();
        return `<tr><td>${title}</td><td>${value}</td></tr>`;
      });
  
      return parsed.join('');
    }
  }
  
customElements.define('kik-product-details', KikProductDetails);
